import {
  GridDashboardTableItem,
  HistoricalUtrs,
  TableOrientation,
  TableWidgetColumnType,
} from '@g17eco/types/insight-custom-dashboard';
import { UtrvInitiativeRef } from '@g17eco/types/universalTrackerValue';
import { extractVariables, tryCalculation } from '@utils/formula';
import {
  collectTableInitiatives,
  disaggregatedCellKey,
  getActualUtrsData,
  getDisplayNumericValue,
  indexUtrvsByInitiativeAndDate,
  isNumericColumn,
  NUMBER_OF_ROWS_TRANSPOSED,
  resolveTableColumn,
  type ResolvedColumn,
} from './utils';
import { getMetricText } from '@features/custom-dashboard/utils/dashboard-utils';
import { DATE, formatDateUTC, sortEffectiveDateAsc } from '@utils/date';
import './TableWidget.scss';
import { getVariablesWithValues } from '@routes/custom-dashboard/items/charts/multi-utrs-chart/utils';
import { truncate } from '@utils/string';
import { SparklineCell } from './SparklineCell';
import { ColumnDef } from '@tanstack/react-table';
import { Table } from '@g17eco/molecules/table';
// MatrixTable emits a real <table> so html2canvas-pro preserves layout in PDF exports.
import { MatrixTable } from '@g17eco/molecules/matrix-table';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

const NUMBER_OF_COLS = 4;
const VALUE_WIDTH = 120;
const UNIT_WIDTH = 80;
const DATE_WIDTH = 110;
const SPARKLINE_WIDTH = 160;
// Narrower period columns in disaggregated mode so several subsidiary groups fit side-by-side.
const DISAGGREGATED_PERIOD_WIDTH = 90;

type TableWidgetProps = Pick<GridDashboardTableItem, 'calculation' | 'variables' | 'config'> & {
  title?: string;
  utrsData: HistoricalUtrs[];
  onClickUtrValueLabel: (utrCode: string) => void;
};

export type TableWidgetData = {
  name: string;
  utrCode: string;
  valueListCode: string | undefined;
  unitText: string;
  [k: string]: string | number | undefined;
};

const buildMergedEffectiveDates = (utrsData: HistoricalUtrs[], orientation: TableOrientation): string[] => {
  const seen = new Set<string>();
  utrsData.forEach((utrData) => {
    utrData.utrvs.forEach((utrv) => {
      if (utrv.effectiveDate) {
        seen.add(utrv.effectiveDate);
      }
    });
  });
  const limit = orientation === TableOrientation.PeriodRows ? NUMBER_OF_ROWS_TRANSPOSED : NUMBER_OF_COLS;
  // Keep the latest N periods rather than the earliest — recent reporting periods are what
  // users care about. `slice(-limit)` drops from the start of the asc-sorted list, so the
  // remaining dates stay in chronological column order (e.g. Mar→Jun, not Jun→Mar).
  return Array.from(seen).sort(sortEffectiveDateAsc).slice(-limit);
};

const indexUtrvsByDate = (utrvs: HistoricalUtrs['utrvs']): Map<string, HistoricalUtrs['utrvs'][number]> => {
  const map = new Map<string, HistoricalUtrs['utrvs'][number]>();
  utrvs.forEach((utrv) => {
    if (utrv.effectiveDate && !map.has(utrv.effectiveDate)) {
      map.set(utrv.effectiveDate, utrv);
    }
  });
  return map;
};

// Single-pass group-by: walks utrvs once and collects per-initiative buckets. Replaces the
// prior O(initiatives × utrvs) of filtering every utrData per initiative.
const buildSlicesByInitiative = (actualUtrsData: HistoricalUtrs[]): Map<string, HistoricalUtrs[]> => {
  const slices = new Map<string, HistoricalUtrs[]>();
  actualUtrsData.forEach((data) => {
    const utrvsByInitiative = new Map<string, HistoricalUtrs['utrvs']>();
    data.utrvs.forEach((utrv) => {
      const id = utrv.initiative?._id;
      if (!id) return;
      const bucket = utrvsByInitiative.get(id);
      if (bucket) bucket.push(utrv);
      else utrvsByInitiative.set(id, [utrv]);
    });
    utrvsByInitiative.forEach((utrvs, id) => {
      const existing = slices.get(id);
      const entry = { ...data, utrvs };
      if (existing) existing.push(entry);
      else slices.set(id, [entry]);
    });
  });
  return slices;
};

type UtrDataEntry = {
  data: HistoricalUtrs;
  // Populated only in disaggregated mode — composite `initiativeId|effectiveDate` cell index.
  utrvIndex?: ReturnType<typeof indexUtrvsByInitiativeAndDate>;
};
type UtrDataByCode = Map<string, UtrDataEntry>;

const buildRowData = ({
  value,
  variables,
  utrDataByCode,
}: {
  value: GridDashboardTableItem['calculation']['values'][number];
  variables: GridDashboardTableItem['variables'];
  utrDataByCode: UtrDataByCode;
}) => {
  const variableLetters = extractVariables(value.formula ?? '');
  const utrCode = variableLetters.length !== 1 ? '' : variables[variableLetters[0]].code;
  const valueListCode = variableLetters.length !== 1 ? '' : variables[variableLetters[0]].valueListCode;
  const entry = utrDataByCode.get(utrCode);
  return { utrCode, valueListCode, utrData: entry?.data, utrvIndex: entry?.utrvIndex };
};

const evaluateCellFormula = ({
  formula,
  utrsData,
  variables,
  utrv,
  resolved,
}: {
  formula: string;
  utrsData: HistoricalUtrs[];
  variables: GridDashboardTableItem['variables'];
  utrv: HistoricalUtrs['utrvs'][number] | undefined;
  resolved: ResolvedColumn;
}) => {
  const { column, valueListCode } = resolved;
  if (column && !isNumericColumn(column.type)) {
    return getMetricText(utrv?.valueData, valueListCode);
  }
  const variablesWithValues = getVariablesWithValues({
    utrsData,
    variables,
    utrvs: utrv ? [utrv] : [],
    fallback: '',
  });
  const calculationValue = tryCalculation({
    formula,
    variables: variablesWithValues,
    fallback: '',
    infinityFallback: '',
  });
  return getDisplayNumericValue(calculationValue) ?? '';
};

const buildNameAndUnitColumns = ({
  title,
  onClickUtrValueLabel,
}: {
  title: string;
  onClickUtrValueLabel: (utrCode: string) => void;
}): ColumnDef<TableWidgetData>[] => [
  {
    id: 'name',
    header: () => <span className='text-capitalize'>{title}</span>,
    meta: {
      headerProps: { className: 'w-100 text-left' },
      cellProps: { className: 'flex-grow-1', style: { width: 'unset' } },
    },
    cell: ({ row }) => (
      <SimpleTooltip text={row.original.name}>
        <div
          className='text-left cell__value-label'
          color='link'
          onClick={() => onClickUtrValueLabel(row.original.utrCode)}
        >
          {row.original.name}
        </div>
      </SimpleTooltip>
    ),
    enableSorting: false,
  },
  {
    id: TableWidgetColumnType.Unit,
    header: () => 'Unit',
    meta: {
      cellProps: {
        className: 'flex-grow-0 text-center ',
        style: { width: UNIT_WIDTH, maxWidth: UNIT_WIDTH, minWidth: UNIT_WIDTH },
      },
    },
    cell: ({ row }) => row.original.unitText,
    enableSorting: false,
  },
];

const getAggregatedTableData = ({
  utrsData,
  variables,
  calculation,
  title,
  onClickUtrValueLabel,
  hiddenColumns,
  actualUtrsData,
  latestEffectiveDates,
  utrDataByCode,
}: Pick<TableWidgetProps, 'variables' | 'calculation'> & {
  utrsData: HistoricalUtrs[];
  title: string;
  onClickUtrValueLabel: (utrCode: string) => void;
  hiddenColumns?: string[];
  actualUtrsData: HistoricalUtrs[];
  latestEffectiveDates: string[];
  utrDataByCode: UtrDataByCode;
}) => {
  const columns: ColumnDef<TableWidgetData>[] = [
    ...buildNameAndUnitColumns({ title, onClickUtrValueLabel }),
    ...latestEffectiveDates.map(
      (effectiveDate, index): ColumnDef<TableWidgetData> => ({
        id: `${effectiveDate}-${index}`,
        header: () => formatDateUTC(effectiveDate, DATE.MONTH_YEAR_SHORT),
        meta: {
          cellProps: {
            className: 'flex-grow-0 text-center text-truncate',
            style: { maxWidth: VALUE_WIDTH, width: VALUE_WIDTH, minWidth: VALUE_WIDTH },
          },
        },
        cell: ({ row }) => row.original[effectiveDate] || '',
        enableSorting: false,
      })
    ),
    {
      id: TableWidgetColumnType.Sparkline,
      header: () => 'Sparkline',
      meta: {
        cellProps: {
          className: 'flex-grow-0 text-center',
          style: { width: SPARKLINE_WIDTH, maxWidth: SPARKLINE_WIDTH, minWidth: SPARKLINE_WIDTH },
        },
      },
      cell: ({ row }) => <SparklineCell rowOriginal={row.original} utrsData={utrsData} />,
      enableSorting: false,
    },
  ];

  const filteredColumns = hiddenColumns ? columns.filter((c) => (c.id ? !hiddenColumns.includes(c.id) : true)) : columns;

  const data: TableWidgetData[] = calculation.values.map((value) => {
    const { utrCode, valueListCode, utrData } = buildRowData({ value, variables, utrDataByCode });
    const utrvByDate = utrData ? indexUtrvsByDate(utrData.utrvs) : undefined;
    const resolved = resolveTableColumn(utrData?.utr, valueListCode);

    const dataByColumns = utrvByDate
      ? latestEffectiveDates.reduce<Record<string, string | number>>((acc, effectiveDate) => {
          acc[effectiveDate] = evaluateCellFormula({
            formula: value.formula || '',
            utrsData: actualUtrsData,
            variables,
            utrv: utrvByDate.get(effectiveDate),
            resolved,
          });
          return acc;
        }, {})
      : {};

    return {
      name: value.name,
      utrCode,
      valueListCode,
      unitText: resolved.unit,
      ...dataByColumns,
    };
  });

  return { filteredColumns, data };
};

const getDisaggregatedTableData = ({
  variables,
  calculation,
  title,
  onClickUtrValueLabel,
  initiatives,
  slicesByInitiative,
  latestEffectiveDates,
  utrDataByCode,
}: Pick<TableWidgetProps, 'variables' | 'calculation'> & {
  title: string;
  onClickUtrValueLabel: (utrCode: string) => void;
  initiatives: UtrvInitiativeRef[];
  slicesByInitiative: Map<string, HistoricalUtrs[]>;
  latestEffectiveDates: string[];
  utrDataByCode: UtrDataByCode;
}) => {
  const columns: ColumnDef<TableWidgetData>[] = [
    ...buildNameAndUnitColumns({ title, onClickUtrValueLabel }),
    ...initiatives.map(
      (initiative): ColumnDef<TableWidgetData> => ({
        id: `initiative-${initiative._id}`,
        header: () => <span className='text-center d-block'>{truncate(initiative.name, 30)}</span>,
        columns: latestEffectiveDates.map(
          (effectiveDate, index): ColumnDef<TableWidgetData> => ({
            id: disaggregatedCellKey(initiative._id, effectiveDate) + `-${index}`,
            header: () => formatDateUTC(effectiveDate, DATE.MONTH_YEAR_SHORT),
            meta: {
              cellProps: {
                className: 'flex-grow-0 text-center text-truncate',
                style: {
                  maxWidth: DISAGGREGATED_PERIOD_WIDTH,
                  width: DISAGGREGATED_PERIOD_WIDTH,
                  minWidth: DISAGGREGATED_PERIOD_WIDTH,
                },
              },
            },
            cell: ({ row }) => row.original[disaggregatedCellKey(initiative._id, effectiveDate)] ?? '',
            enableSorting: false,
          })
        ),
      })
    ),
  ];

  const data: TableWidgetData[] = calculation.values.map((value) => {
    const { utrCode, valueListCode, utrData, utrvIndex } = buildRowData({ value, variables, utrDataByCode });
    const resolved = resolveTableColumn(utrData?.utr, valueListCode);

    const dataByCells: Record<string, string | number> = {};
    if (utrData && utrvIndex) {
      initiatives.forEach((initiative) => {
        const utrsDataByInitiative = slicesByInitiative.get(initiative._id) ?? [];
        latestEffectiveDates.forEach((effectiveDate) => {
          const cellKey = disaggregatedCellKey(initiative._id, effectiveDate);
          dataByCells[cellKey] = evaluateCellFormula({
            formula: value.formula || '',
            utrsData: utrsDataByInitiative,
            variables,
            utrv: utrvIndex.get(cellKey),
            resolved,
          });
        });
      });
    }

    return {
      name: value.name,
      utrCode,
      valueListCode,
      unitText: resolved.unit,
      ...dataByCells,
    };
  });

  return { filteredColumns: columns, data };
};

type TransposedRow = {
  effectiveDate: string;
  [cellKey: string]: string | number;
};

const transposedCellKey = (initiativeId: string | undefined) => initiativeId ?? 'agg';

type TransposedSection = {
  id: string;
  label?: string;
  columns: ColumnDef<TransposedRow>[];
  data: TransposedRow[];
};

const buildDateColumn = (): ColumnDef<TransposedRow> => ({
  id: 'date',
  header: () => 'Date',
  meta: {
    headerProps: { className: 'text-left' },
    cellProps: {
      className: 'text-left',
      style: { width: DATE_WIDTH, minWidth: DATE_WIDTH, maxWidth: DATE_WIDTH },
    },
  },
  cell: ({ row }) => formatDateUTC(row.original.effectiveDate, DATE.MONTH_YEAR_SHORT),
  enableSorting: false,
});

const buildTransposedValueColumn = ({
  id,
  headerText,
  cellKey,
}: {
  id: string;
  headerText: string;
  cellKey: string;
}): ColumnDef<TransposedRow> => ({
  id,
  header: () => headerText,
  meta: {
    cellProps: {
      className: 'text-center text-truncate',
      style: {
        width: DISAGGREGATED_PERIOD_WIDTH,
        minWidth: DISAGGREGATED_PERIOD_WIDTH,
        maxWidth: DISAGGREGATED_PERIOD_WIDTH,
      },
    },
  },
  cell: ({ row }) => row.original[cellKey] ?? '',
  enableSorting: false,
});

const getDisaggregatedTransposedSections = ({
  effectiveDates,
  variables,
  calculation,
  initiatives,
  slicesByInitiative,
  utrDataByCode,
  showUnit,
}: Pick<TableWidgetProps, 'variables' | 'calculation'> & {
  effectiveDates: string[];
  initiatives: UtrvInitiativeRef[];
  slicesByInitiative: Map<string, HistoricalUtrs[]>;
  utrDataByCode: UtrDataByCode;
  showUnit: boolean;
}): TransposedSection[] => {
  return calculation.values.map((value, valueIndex) => {
    const { valueListCode, utrData, utrvIndex } = buildRowData({ value, variables, utrDataByCode });
    const resolved = resolveTableColumn(utrData?.utr, valueListCode);
    const appendUnit = showUnit && resolved.unit !== '';

    const columns: ColumnDef<TransposedRow>[] = [
      buildDateColumn(),
      ...initiatives.map((initiative) =>
        buildTransposedValueColumn({
          id: `${initiative._id}-v${valueIndex}`,
          headerText: truncate(initiative.name, 30),
          cellKey: transposedCellKey(initiative._id),
        })
      ),
    ];

    const data: TransposedRow[] = effectiveDates.map((effectiveDate) => {
      const row: TransposedRow = { effectiveDate };
      initiatives.forEach((initiative) => {
        const utrsDataByInitiative = slicesByInitiative.get(initiative._id) ?? [];
        const cellValue = evaluateCellFormula({
          formula: value.formula || '',
          utrsData: utrsDataByInitiative,
          variables,
          utrv: utrvIndex?.get(disaggregatedCellKey(initiative._id, effectiveDate)),
          resolved,
        });
        row[transposedCellKey(initiative._id)] =
          appendUnit && cellValue !== '' ? `${cellValue} ${resolved.unit}` : cellValue;
      });
      return row;
    });

    return {
      id: `metric-${valueIndex}`,
      label: value.name,
      columns,
      data,
    };
  });
};

export const TableWidget = (props: TableWidgetProps) => {
  const { utrsData, variables, calculation, title = '', onClickUtrValueLabel, config = {} } = props;

  if (!utrsData.length || !variables || !calculation) {
    return (
      <div
        className='w-100 h-100 table-widget__container d-flex align-items-center justify-content-center text-ThemeTextLight'
        data-testid='table-widget-empty'
      >
        No data available
      </div>
    );
  }

  const actualUtrsData = getActualUtrsData(utrsData);
  const initiatives = collectTableInitiatives(actualUtrsData);
  const isDisaggregated = initiatives.length > 1;
  // `period-rows` only takes effect while disaggregated so there's no persisted-but-invisible
  // state when the dashboard reverts to a single subsidiary. Unknown values also coerce here.
  const orientation =
    isDisaggregated && config.orientation === TableOrientation.PeriodRows
      ? TableOrientation.PeriodRows
      : TableOrientation.MetricRows;
  const isTransposed = orientation === TableOrientation.PeriodRows;
  const effectiveDates = buildMergedEffectiveDates(actualUtrsData, orientation);
  const utrDataByCode: UtrDataByCode = new Map(
    actualUtrsData.map((data) => [
      data.utr.code,
      { data, utrvIndex: isDisaggregated ? indexUtrvsByInitiativeAndDate(data.utrvs) : undefined },
    ])
  );
  const slicesByInitiative = isDisaggregated
    ? buildSlicesByInitiative(actualUtrsData)
    : new Map<string, HistoricalUtrs[]>();

  const showUnit = !(config.hiddenColumns ?? []).includes(TableWidgetColumnType.Unit);

  const renderBody = () => {
    if (isTransposed) {
      const sections = getDisaggregatedTransposedSections({
        effectiveDates,
        variables,
        calculation,
        initiatives,
        slicesByInitiative,
        utrDataByCode,
        showUnit,
      });
      return sections.map((section) => (
        <div
          key={section.id}
          className='table-widget__section'
          data-testid='table-widget-section'
          data-section-id={section.id}
        >
          {section.label ? (
            <div className='text-sm text-ThemeTextLight mb-2' data-testid='table-widget-section-label'>
              {section.label}
            </div>
          ) : null}
          <MatrixTable columns={section.columns} data={section.data} />
        </div>
      ));
    }

    const { filteredColumns, data } = isDisaggregated
      ? getDisaggregatedTableData({
          variables,
          calculation,
          title,
          onClickUtrValueLabel,
          initiatives,
          slicesByInitiative,
          latestEffectiveDates: effectiveDates,
          utrDataByCode,
        })
      : getAggregatedTableData({
          utrsData,
          variables,
          calculation,
          title,
          onClickUtrValueLabel,
          hiddenColumns: config.hiddenColumns,
          actualUtrsData,
          latestEffectiveDates: effectiveDates,
          utrDataByCode,
        });
    return isDisaggregated ? (
      <MatrixTable columns={filteredColumns} data={data} />
    ) : (
      <Table columns={filteredColumns} data={data} />
    );
  };

  return (
    <div
      className={`w-100 h-100 table-widget__container${isTransposed ? ' d-flex flex-column' : ''}`}
      style={isTransposed ? { gap: '1.5rem' } : undefined}
      data-disaggregated={isDisaggregated || undefined}
      data-orientation={orientation}
      data-testid='table-widget-container'
    >
      {renderBody()}
    </div>
  );
};
